import * as Sentry from "@sentry/vue";

type MonitoringPayload = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, string | number | boolean>>;
  }
}

interface MonitoringContext {
  user: {
    id?: string;
    username?: string;
  } | null;
  tags: Record<string, string>;
  appState: Record<string, string>;
}

interface CaptureExceptionOptions {
  tags?: Record<string, string>;
  context?: MonitoringPayload;
}

const STORAGE_KEYS = [
  "evaluation_id",
  "thread_id",
  "company_id",
  "batch_id",
  "employee_code",
  "employee_name",
  "sub",
  "unionid",
];

const normalizeValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  return String(value);
};

const normalizePayload = (payload: MonitoringPayload = {}): Record<string, string | number | boolean> => {
  return Object.entries(payload).reduce<Record<string, string | number | boolean>>((acc, [key, value]) => {
    if (value === null || value === undefined) {
      return acc;
    }

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      acc[key] = value;
      return acc;
    }

    acc[key] = JSON.stringify(value);
    return acc;
  }, {});
};

const pushDataLayerEvent = (
  name: string,
  payload: Record<string, string | number | boolean>,
): void => {
  if (!import.meta.client) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: name,
    ...payload,
  });
};

const safeParseJson = <T>(raw: string | null): T | null => {
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const getMonitoringContextFromStorage = (): MonitoringContext => {
  if (!import.meta.client) {
    return {
      user: null,
      tags: {},
      appState: {},
    };
  }

  const practiceUser = safeParseJson<{ id?: number; username?: string; real_name?: string }>(
    localStorage.getItem("practice_account_info"),
  );
  const wechatUser = safeParseJson<{ nickname?: string }>(localStorage.getItem("userInfo"));
  const user = practiceUser
    ? {
        id: normalizeValue(practiceUser.id),
        username: practiceUser.real_name || practiceUser.username,
      }
    : {
        id: normalizeValue(localStorage.getItem("unionid") || localStorage.getItem("employee_code")),
        username:
          localStorage.getItem("user_name") ||
          localStorage.getItem("employee_name") ||
          wechatUser?.nickname,
      };

  const tags = STORAGE_KEYS.reduce<Record<string, string>>((acc, key) => {
    const value = normalizeValue(localStorage.getItem(key));
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {});

  const currentPath = normalizeValue(window.location.pathname);
  if (currentPath) {
    tags.current_path = currentPath;
  }

  return {
    user: user.id || user.username ? user : null,
    tags,
    appState: {
      current_path: currentPath || "",
      locale: normalizeValue(localStorage.getItem("app_locale_preference")) || "",
      has_practice_token: localStorage.getItem("practice_account_token") ? "true" : "false",
    },
  };
};

export const syncMonitoringScopeFromStorage = (): void => {
  if (!import.meta.client) {
    return;
  }

  const context = getMonitoringContextFromStorage();

  Sentry.setUser(context.user);

  Object.entries(context.tags).forEach(([key, value]) => {
    Sentry.setTag(key, value);
  });

  Sentry.setContext("app_state", context.appState);
};

export const trackMonitoringEvent = (name: string, payload: MonitoringPayload = {}): void => {
  const normalizedPayload = normalizePayload(payload);

  Sentry.addBreadcrumb({
    category: "app.event",
    type: "default",
    level: "info",
    message: name,
    data: normalizedPayload,
  });

  pushDataLayerEvent(name, normalizedPayload);
};

export const trackLocaleDiagnostics = (
  source: string,
  payload: MonitoringPayload = {},
): void => {
  if (!import.meta.client) {
    return;
  }

  const practiceUser = safeParseJson<{ id?: number; language?: string | null }>(
    localStorage.getItem("practice_account_info"),
  );

  trackMonitoringEvent("locale_diagnostics", {
    source,
    uid: normalizeValue(practiceUser?.id) || "",
    system_language: navigator.language || "",
    system_languages: navigator.languages?.join(",") || "",
    cached_language: localStorage.getItem("app_locale_preference") || "",
    profile_language: normalizeValue(practiceUser?.language) || "",
    current_path: window.location.pathname || "",
    has_practice_token: Boolean(localStorage.getItem("practice_account_token")),
    ...payload,
  });
};

export const captureMonitoringException = (
  error: unknown,
  options: CaptureExceptionOptions = {},
): string => {
  return Sentry.withScope((scope) => {
    Object.entries(options.tags || {}).forEach(([key, value]) => {
      scope.setTag(key, value);
    });

    if (options.context) {
      scope.setContext("details", normalizePayload(options.context));
    }

    return Sentry.captureException(error);
  });
};

export const captureMonitoringMessage = (
  message: string,
  payload: MonitoringPayload = {},
): string => {
  return Sentry.withScope((scope) => {
    scope.setContext("details", normalizePayload(payload));
    return Sentry.captureMessage(message);
  });
};
